/**
 * Retirement Scenario Manager
 * Handles saving, loading, and comparing retirement planning scenarios
 */

const STORAGE_KEY = 'retirement_scenarios';
const MAX_SCENARIOS = 10; // Keep last 10 scenarios

/**
 * Save a retirement scenario to localStorage
 */
export function saveScenario(scenarioName, inputs, results) {
  try {
    const scenarios = loadScenarios();

    const newScenario = {
      id: Date.now().toString(),
      name: scenarioName || `Scenario ${new Date().toLocaleString()}`,
      timestamp: new Date().toISOString(),
      inputs: {
        // Core parameters
        currentAge: inputs.currentAge,
        retirementAge: inputs.retirementAge,
        lifeExpectancy: inputs.lifeExpectancy,
        desiredAnnualIncome: inputs.desiredAnnualIncome,
        currentAnnualIncome: inputs.currentAnnualIncome,
        essentialSpending: inputs.essentialSpending,
        discretionarySpending: inputs.discretionarySpending,

        // Investment parameters
        inflationRate: inputs.inflationRate,
        expectedReturn: inputs.expectedReturn,
        volatility: inputs.volatility,
        riskProfile: inputs.riskProfile,
        withdrawalStrategy: inputs.withdrawalStrategy,

        // State pension
        includeStatePension: inputs.includeStatePension,
        includeSpouseStatePension: inputs.includeSpouseStatePension,
        statePensionAge: inputs.statePensionAge,

        // Income sources
        rentalProperties: inputs.rentalProperties || [],
        partTimeWork: inputs.partTimeWork || null,
        annuities: inputs.annuities || [],
        otherIncome: inputs.otherIncome || [],
        spouseIncome: inputs.spouseIncome || null,

        // Other
        oneOffExpenses: inputs.oneOffExpenses || [],
        ukTaxFreeLumpSumOptions: inputs.ukTaxFreeLumpSumOptions || {},
        country: inputs.country
      },
      results: {
        // Key metrics
        totalAtRetirement: results.accumulationResult?.totalAtRetirement || 0,
        depletionAge: results.drawdownResult?.depletionAge || 0,
        finalBalance: results.drawdownResult?.finalBalance || 0,
        successRate: results.monteCarloResult?.successRate || 0,

        // Detailed metrics
        fundingRatio: results.metrics?.fundingRatio || 0,
        requiredPortfolio: results.metrics?.requiredPortfolio || 0,
        surplusDeficit: results.metrics?.surplusDeficit || 0,
        replacementRate: results.metrics?.replacementRate || 0,

        // State pension estimate
        statePensionEstimate: results.statePensionEstimate || 0,

        // Store first 10 years of projections for comparison charts
        drawdownProjections: results.drawdownResult?.projections?.slice(0, 10) || []
      }
    };

    // Add new scenario at the beginning
    scenarios.unshift(newScenario);

    // Keep only last MAX_SCENARIOS
    const trimmedScenarios = scenarios.slice(0, MAX_SCENARIOS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedScenarios));

    return newScenario;
  } catch (error) {
    console.error('Error saving scenario:', error);
    return null;
  }
}

/**
 * Load all saved scenarios from localStorage
 */
export function loadScenarios() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading scenarios:', error);
    return [];
  }
}

/**
 * Delete a specific scenario
 */
export function deleteScenario(scenarioId) {
  try {
    const scenarios = loadScenarios();
    const filtered = scenarios.filter(s => s.id !== scenarioId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting scenario:', error);
    return false;
  }
}

/**
 * Get the last N scenarios for comparison
 */
export function getRecentScenarios(count = 3) {
  const scenarios = loadScenarios();
  return scenarios.slice(0, count);
}

/**
 * Clear all saved scenarios
 */
export function clearAllScenarios() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing scenarios:', error);
    return false;
  }
}

/**
 * Load a scenario's inputs back into the planner
 */
export function loadScenarioInputs(scenarioId) {
  const scenarios = loadScenarios();
  const scenario = scenarios.find(s => s.id === scenarioId);
  return scenario ? scenario.inputs : null;
}

/**
 * Rename a scenario
 */
export function renameScenario(scenarioId, newName) {
  try {
    const scenarios = loadScenarios();
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (scenario) {
      scenario.name = newName;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error renaming scenario:', error);
    return false;
  }
}

/**
 * Compare scenarios and return differences
 */
export function compareScenarios(scenarioIds) {
  const scenarios = loadScenarios();
  const selected = scenarios.filter(s => scenarioIds.includes(s.id));

  if (selected.length < 2) {
    return null;
  }

  // Calculate differences
  const comparison = {
    scenarios: selected,
    differences: {
      retirementAge: selected.map(s => s.inputs.retirementAge),
      totalAtRetirement: selected.map(s => s.results.totalAtRetirement),
      depletionAge: selected.map(s => s.results.depletionAge),
      successRate: selected.map(s => s.results.successRate),
      fundingRatio: selected.map(s => s.results.fundingRatio),
      withdrawalStrategy: selected.map(s => s.inputs.withdrawalStrategy)
    }
  };

  return comparison;
}

/**
 * Export scenarios as JSON file
 */
export function exportScenarios() {
  const scenarios = loadScenarios();
  const dataStr = JSON.stringify(scenarios, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `retirement-scenarios-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Import scenarios from JSON file
 */
export function importScenarios(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        if (Array.isArray(imported)) {
          const existing = loadScenarios();
          const combined = [...imported, ...existing].slice(0, MAX_SCENARIOS);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(combined));
          resolve(imported.length);
        } else {
          reject(new Error('Invalid file format'));
        }
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
}
